import { useState } from 'react';
import { Card, KpiCard, AsyncBoundary, ListPage, Badge } from '@kollez/design-system';
import { formatLPA, formatINR } from '@kollez/shared';
import { useGroupAnalytics, useGroupAnalyticsTrends, useNirfExport, type GroupAnalyticsTrends } from '@/lib/queries';
import { useToast } from '@kollez/design-system';

export function GroupAnalyticsPage() {
  const { data, isLoading, error, refetch } = useGroupAnalytics();
  const nirf = useNirfExport();
  const toast = useToast();

  async function runNirf() {
    try {
      const r = await nirf.mutateAsync();
      toast.show({ title: 'NIRF export queued', message: `Ready around ${new Date(r.readyBy).toLocaleTimeString('en-IN')}` });
    } catch {
      toast.show({ tone: 'warning', message: 'Could not queue NIRF export' });
    }
  }

  const totals = data?.totals;
  const rows = data?.colleges ?? [];

  return (
    <div className="stack">
      {/* F-080 pattern: page-header lives outside AsyncBoundary so the h1 is always present. */}
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Group analytics</p>
          <h1 className="h-page">Placement performance across your colleges</h1>
          <p className="subhead">Live from your drives, offers and applications.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={runNirf} disabled={nirf.isPending}>
            {nirf.isPending ? 'Queuing…' : 'Export NIRF report'}
          </button>
        </div>
      </div>
      <AsyncBoundary
        kind="table"
        loading={isLoading}
        error={error}
        empty={!isLoading && rows.length === 0}
        emptyTitle="No colleges yet"
        emptyHint="Add colleges under Structure to see placement analytics here."
        onRetry={() => void refetch()}
      >
      <div className="stack">

        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <KpiCard label="Colleges" value={totals?.colleges ?? 0} />
          <KpiCard label="Drives run" value={totals?.drives ?? 0} tone="info" />
          <KpiCard label="Offers accepted" value={totals?.offersAccepted ?? 0} tone="success" />
          <KpiCard label="Applications" value={(totals?.applications ?? 0).toLocaleString('en-IN')} />
        </div>

        <TrendsCard />

        <ListPage
          eyebrow="By college"
          title="League table"
          rows={rows}
          rowKey={(r) => r._id}
          columns={[
            { key: 'name', label: 'College', render: (r) => <div><div className="cell-title">{r.name}</div><div className="cell-sub">{r.code} · {r.type}</div></div> },
            { key: 'd', label: 'Drives', right: true, render: (r) => r.drives },
            { key: 'oi', label: 'Offers issued', right: true, hideMobile: true, render: (r) => r.offersIssued },
            { key: 'oa', label: 'Accepted', right: true, render: (r) => <Badge tone={r.offersAccepted > 0 ? 'success' : 'outline'}>{r.offersAccepted}</Badge> },
            { key: 'avg', label: 'Avg CTC', right: true, hideMobile: true, render: (r) => (r.avgCtc ? formatLPA(r.avgCtc) : '—') },
            { key: 'max', label: 'Max CTC', right: true, hideMobile: true, render: (r) => (r.maxCtc ? formatINR(r.maxCtc) : '—') },
          ]}
        />
      </div>
      </AsyncBoundary>
    </div>
  );
}

/**
 * Pass 11 relationship endpoint /group/analytics/trends — daily-bucketed
 * applications / offersAccepted / interviews across the group's tenant scope,
 * with 7d / 30d / 90d windows. Rendered as three inline SVG line charts sharing
 * the same time axis. Empty-state when no series has any activity yet.
 */
function TrendsCard() {
  const [win, setWin] = useState<'7d' | '30d' | '90d'>('30d');
  const q = useGroupAnalyticsTrends(win);
  const data = q.data;

  return (
    <Card padding="md">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h2 className="h-section">Daily trends</h2>
          <p className="cell-sub m-0">
            Applications, interviews and accepted offers over the selected window.
          </p>
        </div>
        <div className="flex gap-1">
          {(['7d', '30d', '90d'] as const).map((w) => (
            <button
              key={w}
              type="button"
              className={`tag ${win === w ? 'tag-active' : ''}`}
              aria-pressed={win === w}
              onClick={() => setWin(w)}
            >
              {w}
            </button>
          ))}
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        inCard
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && data
          ? (data.applications.length + data.offersAccepted.length + data.interviews.length) === 0
          : false}
        emptyTitle="No activity in this window"
        emptyHint="Try a wider window, or come back once drives start collecting applications."
        onRetry={() => void q.refetch()}
      >
        {data && (
          <div className="grid gap-4 mt-3 md:grid-cols-3">
            <SeriesChart title="Applications" tone="info" series={data.applications} />
            <SeriesChart title="Interviews" tone="warning" series={data.interviews} />
            <SeriesChart title="Offers accepted" tone="success" series={data.offersAccepted} />
          </div>
        )}
      </AsyncBoundary>
    </Card>
  );
}

function SeriesChart({
  title,
  tone,
  series,
}: {
  title: string;
  tone: 'info' | 'success' | 'warning';
  series: GroupAnalyticsTrends['applications'];
}) {
  const total = series.reduce((n, r) => n + r.count, 0);
  const strokeVar = tone === 'info' ? '--ink-accent' : tone === 'success' ? '--ink-success' : '--ink-warning';
  const w = 320;
  const h = 90;
  const pad = 4;

  let path = '';
  if (series.length > 0) {
    const max = Math.max(1, ...series.map((r) => r.count));
    const step = series.length > 1 ? (w - pad * 2) / (series.length - 1) : 0;
    path = series.map((r, i) => {
      const x = pad + i * step;
      const y = h - pad - (r.count / max) * (h - pad * 2);
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(' ');
  }

  return (
    <div className="card card-pad">
      <div className="flex items-baseline justify-between mb-1">
        <div className="cell-sub">{title}</div>
        <div className="text-sm"><b>{total.toLocaleString('en-IN')}</b></div>
      </div>
      {series.length === 0 ? (
        <div className="cell-sub" style={{ height: h }}>No activity</div>
      ) : (
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" role="img" aria-label={`${title} trend`}>
          <path
            d={path}
            fill="none"
            stroke={`rgb(var(${strokeVar}))`}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      )}
    </div>
  );
}
